import { useState } from 'react';
import {
  DefaultNodeOptions,
  ObjectNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';
import classNames from 'clsx';

import { Button } from '../../../components/buttons/Button';
import { FormField } from '../../../components/formfield/FormField';
import { GoogleFontIcon } from '../../../components/icons/GoogleFontIcon';
import { SectionHeader } from '../../../components/sectionheader/SectionHeader';

import { ColumnFormatObjectProperties } from './ColumnFormatObjectProperties';
import { ObjectProperties } from './ObjectProperties';
import { TableFormatObjectProperties } from './TableFormatObjectProperties';

export type ObjectOptions = DefaultNodeOptions<{
  /** if set, will add an accordion in the given toggle state */
  collapsed?: boolean;
  /** if true will add a separator line to the header */
  headerSeparator?: boolean;
  /** header font size relative to 1 (em). Defaults to 1 */
  headerFontSize?: number;
  /** if true, the title won't be displayed */
  hideTitle?: boolean;
  /** if set to false, errors won't be shown. Defaults to true */
  showErrors?: boolean;
  /**
   * Indicates if this object itself is used within a table layout.
   * If set to true:
   *  - The object will hide the header
   *  - The object will format its content in table layout as well
   *
   * These measures will basically "flatten" the nested properties within the
   * table layout.
   * */
  tableLayout?: boolean;
  /** if set to true, fields will be shown horizontally */
  columnLayout?: boolean;
  /** show loading indicators on child fields */
  loading?: boolean;
}>;

/**
 * @note that a lot of functionality has been removed here, especially adding or
 * deleting optional properties and the edit-json action. Follow the rje-widgets
 * implementation in case this will be needed.
 */
export const ObjectWidget = widget<ObjectNode<ObjectOptions>>(
  ({ node, options, editor }) => {
    const [showContent, setShowContent] = useState<boolean>(
      options.collapsed ? !options.collapsed : true
    );
    const { title, description } = options;
    const showTitle = !options.hideTitle;
    const showErrors = options.showErrors ?? true;
    const tableLayout = options.tableLayout ?? false;
    const columnLayout = options.columnLayout ?? false;

    const showHeader =
      !tableLayout &&
      !columnLayout &&
      (title || description || options.collapsed != null);

    const renderJsonObject = () => {
      switch (node.schema.format) {
        case 'table':
          return (
            <TableFormatObjectProperties
              node={node}
              options={options}
              editor={editor}
            />
          );
        case 'column':
          return (
            <ColumnFormatObjectProperties
              node={node}
              options={options}
              editor={editor}
            />
          );
        default:
          return (
            <ObjectProperties editor={editor} node={node} options={options} />
          );
      }
    };

    return (
      <FormField
        widgetType="object"
        node={node}
        options={options}
        showError={false}
        tableLayout={tableLayout}
      >
        {showHeader && (
          <FormField.Header>
            <SectionHeader>
              {options.collapsed != null && (
                <Button
                  variant="text"
                  className={classNames(
                    'rje-widget-action',
                    showContent
                      ? 'rje-widget-action--uncollapsed'
                      : 'rje-widget-action--collapsed'
                  )}
                  onClick={() => setShowContent(!showContent)}
                  icon={
                    <GoogleFontIcon
                      name={showContent ? 'expand_more' : 'expand_less'}
                    />
                  }
                />
              )}
              {showTitle && (
                <SectionHeader.Label
                  title={options.title}
                  size={options.headerFontSize}
                  separator={options.headerSeparator === true}
                />
              )}
            </SectionHeader>
            <FormField.Description>{description}</FormField.Description>
            {showErrors && <FormField.Error errors={node.errors} />}
          </FormField.Header>
        )}
        {showContent && renderJsonObject()}
      </FormField>
    );
  }
);

export const ObjectWidgetPlugin: WidgetPlugin = {
  id: 'object-widget',
  use: (node) => node.schema.type === 'object',
  Widget: ObjectWidget,
};
